import { useCallback } from 'react'
import { parse, stringify } from 'query-string'
import { navigate } from 'gatsby'
import { LocationSearch, Nav } from './useQueryString'

export default function useStableNav(): {
  nav: Nav
  relocate: (pathName: string) => void
  setupGuideNav: (search: LocationSearch, shouldMaintainState?: boolean) => void
} {
  const location = window.location
  const qs = parse(location.search) as LocationSearch

  const nav = useCallback(
    (
      search: LocationSearch,
      options?: {
        shouldReplaceHistoryEntry?: boolean
        shouldMaintainState?: boolean
        shouldLeaveQueryString?: boolean
      },
    ): void => {
      const { shouldReplaceHistoryEntry = false, shouldLeaveQueryString = false } = options ?? {}

      // Avoiding to inject "?" if no search parameters was passed.
      const to = Object.keys(search).length
        ? `${location.pathname}?${stringify({
            ...(shouldLeaveQueryString ? { ...qs, ...search } : search),
          })}`
        : location.pathname

      navigate(to, {
        replace: shouldReplaceHistoryEntry,
      })
    },
    [location.pathname, qs],
  )

  const relocate = useCallback(
    (pathName: string): void => {
      navigate(`${pathName}${location.search}`)
    },
    [location],
  )

  // preserves the `freeResolverType` query parameter (used for opening help pane
  // for free resolvers on homepage), if present
  const setupGuideNav = useCallback(
    (params?: LocationSearch, shouldMaintainState = false): void => {
      const { freeResolverType, resolverUid, orgId } = qs
      nav(
        {
          freeResolverType,
          resolverUid,
          orgId,
          ...params,
        },
        { shouldMaintainState },
      )
    },
    [nav, qs],
  )

  return { nav, relocate, setupGuideNav }
}
